// Update <model> from Hugging Face (acceptance L1–L5) — only ever on the admin's
// click (D5). "pick": look a version up and download it alongside the current one.
// "switch": a downloaded version is waiting while the model runs — when to switch.
import { useState } from "react";
import { modelServingApi } from "../../api/client";
import { COLORS } from "../../theme/colors";
import { activeRevision, shortSha, updateCheck } from "./modelServingModel";
import { Btn, Dialog, Field, MONO, Notice, errorText, input } from "./modelServingUi";

function SwitchChoice({ model, onClose, onDone }) {
  const [policy, setPolicy] = useState("when_idle");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const pending = (model.revisions || []).find((r) => r.id === model.pending_revision_id);
  const go = async () => {
    setBusy(true); setErr(null);
    try {
      await modelServingApi.switchVersion(model.key, model.pending_revision_id, policy);
      onDone?.();
      onClose();
    } catch (e) {
      setErr(errorText(e));
      setBusy(false);
    }
  };
  const choice = (value, title, text) => (
    <label style={{ display: "flex", gap: 10, padding: "10px 12px", borderRadius: 12, cursor: "pointer",
      border: `1px solid ${policy === value ? COLORS.accent : COLORS.border}` }}>
      <input type="radio" name="ms-switch" value={value} checked={policy === value} onChange={() => setPolicy(value)} />
      <span><b style={{ fontSize: 13 }}>{title}</b>
        <small style={{ display: "block", color: COLORS.textDim, fontSize: 11.5, marginTop: 2 }}>{text}</small></span>
    </label>
  );
  return (
    <Dialog testid="switch-dialog" title={`Update ${model.display_name} from Hugging Face`} glyph="↑" maxWidth={520}
      onClose={onClose}
      sub="Downloads the version you pick into your S3 library. The model keeps serving its current version until you switch."
      footer={(
        <>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn kind="primary" disabled={busy} onClick={go}>Switch version</Btn>
        </>
      )}>
      <p style={{ margin: "0 0 10px", fontSize: 13, color: COLORS.textMuted }}>
        <b style={{ color: COLORS.text }}>{shortSha(pending?.sha)} is fully downloaded.</b> The model is running, so
        switching restarts it.
      </p>
      <div data-lc="switch-choice" style={{ display: "grid", gap: 8, marginBottom: 10 }}>
        {choice("when_idle", "Switch when idle", "Applies the next time the model parks. No interruption for current users.")}
        {choice("now", "Switch now", "Restarts the model on the new version. Requests wait ~3–5 min while it warms up.")}
      </div>
      <p style={{ fontSize: 12, color: COLORS.textDim, margin: "0 0 8px" }}>
        The current version is kept so you can roll back from Versions.
      </p>
      {err && <Notice testid="switch-error" tone="bad" icon="!">{err}</Notice>}
    </Dialog>
  );
}

function PickVersion({ model, onClose, onDone }) {
  const [rev, setRev] = useState("");
  const [busy, setBusy] = useState(false);
  const [check, setCheck] = useState(null);
  const [err, setErr] = useState(null);
  const revision = rev.trim() || "main";

  const lookup = async () => {
    setBusy(true); setErr(null); setCheck(null);
    try {
      const r = await modelServingApi.lookup(model.hf_repo, revision);
      setCheck(updateCheck(r.data?.model, model));
    } catch (e) {
      setErr(e?.configRequired
        ? `Hugging Face needs a token for this repo: ${e.configRequired.message}. Add one in Admin → Integrations → Hugging Face.`
        : errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await modelServingApi.update(model.key, revision);
      if (r.data?.already_current) {
        setCheck({ same: true });
        setBusy(false);
        return;
      }
      onDone?.();
      onClose();
    } catch (e) {
      setErr(errorText(e));
      setBusy(false);
    }
  };

  return (
    <Dialog testid="update-dialog" title={`Update ${model.display_name} from Hugging Face`} glyph="↑" maxWidth={520}
      onClose={onClose}
      sub="Downloads the version you pick into your S3 library. The model keeps serving its current version until you switch."
      footer={(
        <>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn kind="primary" disabled={busy || !check || check.same} onClick={download}>Download version</Btn>
        </>
      )}>
      <Field label="Version" htmlFor="ms-upd-rev"
        hint="Leave as latest, or enter a branch, tag or commit you saw on huggingface.co.">
        <div style={{ display: "flex", gap: 8 }}>
          <input id="ms-upd-rev" data-lc="update-version-pick" style={{ ...input(), fontFamily: MONO }} value={rev}
            placeholder="latest (main)" onChange={(e) => { setRev(e.target.value); setCheck(null); }} />
          <Btn disabled={busy} onClick={lookup}>{busy ? "Looking up…" : "Look up"}</Btn>
        </div>
      </Field>
      {check && !check.same && (
        <Notice testid="update-found" tone="ok" icon="✓">
          <b style={{ color: COLORS.text }}>{check.text}</b> Currently serving{" "}
          <span style={{ fontFamily: MONO }}>{shortSha(activeRevision(model)?.sha)}</span>.
        </Notice>
      )}
      {check?.same && (
        <Notice lc="update-same-version" tone="info" icon="🛈">
          <b style={{ color: COLORS.text }}>Already on this version.</b>{" "}
          {rev.trim() || "latest (main)"} resolves to the version this model is serving. Nothing to download.
        </Notice>
      )}
      {err && <Notice testid="update-error" tone="bad" icon="!">{err}</Notice>}
    </Dialog>
  );
}

export default function UpdateDialog({ mode = "pick", ...props }) {
  return mode === "switch" ? <SwitchChoice {...props} /> : <PickVersion {...props} />;
}
